import { motion } from 'framer-motion'
import { Users, BarChart3, Shield, User, RefreshCw, LogOut, LinkIcon, Menu, X, Mail, CreditCard, Database, HardDrive, UserCheck, Key, ClockIcon } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { adminAuthService } from '../services/adminAuth'
import type { AdminUser } from '../services/adminAuth'

interface AdminHeaderProps {
  title: string
  subtitle?: string
  showRefresh?: boolean
  onRefresh?: () => void
  isRefreshing?: boolean
  lastUpdated?: Date | null
}

export default function AdminHeader({ 
  title, 
  subtitle, 
  showRefresh = false, 
  onRefresh, 
  isRefreshing = false, 
  lastUpdated 
}: AdminHeaderProps) {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const user = adminAuthService.getCurrentUser()
    setAdminUser(user)
  }, [])

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true)
      await adminAuthService.logout()
      toast.success('Logged out successfully')
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
      toast.error('Logout failed')
    } finally {
      setIsLoggingOut(false)
    }
  }

  const handleRefresh = async () => {
    if (onRefresh) {
      await onRefresh()
    }
  }

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/',
      icon: BarChart3,
      current: location.pathname === '/'
    },
    {
      name: 'Analytics', 
      href: '/analytics',
      icon: BarChart3,
      current: location.pathname === '/analytics'
    },
    {
      name: 'User Management',
      href: '/users',
      icon: Users,
      current: location.pathname.startsWith('/users')
    },
    {
      name: 'Links',
      href: '/links',
      icon: LinkIcon,
      current: location.pathname === '/links'
    },
    {
      name: 'API Management',
      href: '/api-management',
      icon: Key,
      current: location.pathname === '/api-management'
    },
    {
      name: 'Plan Management',
      href: '/plans',
      icon: CreditCard,
      current: location.pathname === '/plans'
    },
    {
      name: 'Plan History',
      href: '/plan-history',
      icon: ClockIcon,
      current: location.pathname === '/plan-history'
    },
    {
      name: 'Subscription Management',
      href: '/subscriptions',
      icon: UserCheck,
      current: location.pathname === '/subscriptions'
    },
    {
      name: 'Email Management',
      href: '/email',
      icon: Mail,
      current: location.pathname === '/email'
    },
    {
      name: 'Backup Management',
      href: '/backup',
      icon: Database,
      current: location.pathname === '/backup'
    },
    {
      name: 'Capacity Management',
      href: '/capacity',
      icon: HardDrive,
      current: location.pathname === '/capacity'
    },
    {
      name: 'Security',
      href: '/security',
      icon: Shield,
      current: location.pathname === '/security' || location.pathname.startsWith('/security/'),
      subItems: [
        {
          name: 'Security Settings',
          href: '/security',
          current: location.pathname === '/security'
        },
        {
          name: 'Security Logs',
          href: '/security/logs',
          current: location.pathname === '/security/logs'
        }
      ]
    }
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header */}
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo and Navigation */}
          <div className="flex items-center space-x-8">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <div className="h-8 w-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">S</span>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">
                  Shorty Admin
                </span>
              </Link>
            </div>
            
            {/* Navigation Menu */}
            <nav className="hidden md:flex space-x-6">
              {navigationItems.map((item) => (
                <div key={item.name} className="relative group">
                  <Link
                    to={item.href}
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 ${
                      item.current
                        ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/50 border-2 border-indigo-600 dark:border-indigo-400'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                  </Link>
                  
                  {/* Tooltip */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap">
                    {item.name}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900 dark:border-b-gray-700"></div>
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* Right side - Admin info and actions */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            {showRefresh && onRefresh && (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="hidden sm:inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
            
            <button 
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {isLoggingOut ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              ) : (
                <LogOut className="h-4 w-4 mr-2" />
              )}
              <span className="hidden sm:inline">
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </span>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-gray-200 dark:border-gray-700"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    item.current
                      ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/50'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile admin info */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
                <div className="flex items-center px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <User className="h-4 w-4 mr-2" />
                  <span>{adminUser?.name || adminUser?.email || 'Admin'}</span>
                </div>
                
                {showRefresh && onRefresh && (
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="w-full flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Page Title Section */}
        <div className="py-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
              {adminUser && (
                <p className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                  Logged in as: {adminUser.email}
                </p>
              )}
              {lastUpdated && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
